import React, { FC, useEffect, useState } from "react";

import {
  Table,
  Row,
  Col,
  Button,
  Input,
  message,
  Select,
  Avatar,
  Space,
  Modal,
  Drawer,
} from "antd";

import { DeleteOutlined, ExclamationCircleOutlined, OrderedListOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { PagedResult } from "states/paged-result-model";
import { useDebouncedCallback } from "use-debounce/lib";

import {
  SearchProductRequestModel,
  ProductModel,
} from "states/product/product.models";
import {
  getAllVariationsApi,
  removeProduct,
  searchListProductApi,
  searchProductApi,
  updateSortProductApi,
} from "states/product/product.api";
import { Link } from "react-router-dom";
import { ProductCategoryModel } from "states/product-category/product-category.models";
import { getProductCategoryListApi } from "states/product-category/product-category.api";
import { t, CoreResource } from "shared/plugins/localization";
import {
  DefaultErrorMessage,
  ListText,
  ProductText,
  Sort,
} from "shared/constants/define-localization";
import { RemovedEntityModel } from "shared/models/removed-entity-model";
import {
  formatMoney,
  redirectFileLocalUrl,
} from "shared/plugins/string-helper";
import {
  ADD_PRODUCT_ROUTE,
  UPDATE_PRODUCT_ROUTE,
} from "shared/constants/routes";
import { VariationModal } from "./components/variation-modal";
import { Sortable, SortItemModel } from "shared/components/sortable/sortable";
import { UpdatedSortEntityModel } from "shared/models/updated-sort-entity-model";

interface IProductProps {}
const { Option } = Select;
const Product: FC<IProductProps> = () => {
  const [selectedRowsKeys, setSelectedRowsKeys] = useState<
    RemovedEntityModel<number>
  >({
    idList: [],
  });
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [pagedResults, setPagedResults] = useState(
    {} as PagedResult<ProductModel>
  );
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [isUpdateSort, setIsUpdateSort] = useState(false);
  const [filterModel, setFilterModel] = useState<SearchProductRequestModel>({
    includeAccessory: true,
    includeChildrenUnitProduct: false,
    includeChildrenVariationProduct: false,
    page: 1,
    pageSize: 20,
  });
  const [productCategories, setProductCategories] = useState<
    ProductCategoryModel[]
  >([]);

  const [variationModal, setVariationModal] = useState<{
    model: ProductModel[];
    visible: boolean;
    onClose: (isLoadingList?: boolean) => void;
  }>({
    model: [] as ProductModel[],
    visible: false,
    onClose: (isLoadingList?: boolean) => {
      setVariationModal({ ...variationModal, visible: false });
      if (isLoadingList) {
        gotoPage(1,20);
      }
    },
  });

  const rowSelection = {
    selectedRowsKeys,
    onChange: (selectedRowKeys: any) => {
      setSelectedRowsKeys({ ...selectedRowsKeys, idList: selectedRowKeys });
    },
  };
  const handleSortProduct = async (sortList: SortItemModel[]) => {
    try {
      let payload = sortList.map(
        (item) =>
          ({
            id: Number(item.key),
            sort: item.sort,
          } as UpdatedSortEntityModel<number>)
      );
      await updateSortProductApi(payload);
      message.success("Sắp xếp thành công");
      handleSearch();
    } catch {
      message.error("Lỗi hệ thống");
    }
  };
  const getProductCategories = async () => {
    try {
      const res = await getProductCategoryListApi();
      setProductCategories(res.data);
    } catch (error) {
      message.error(error?.response?.data?.error?.message ?? "Lỗi hệ thống");
    }
  };
  const onEmptySelection = () => {
    setSelectedRowsKeys({ ...selectedRowsKeys, idList: [] });
  };
  const onRemoveBtn = () => {
    Modal.confirm({
      title: 'Thông báo',
      icon: <ExclamationCircleOutlined />,
      content: 'Bạn có muốn xóa sản phẩm này không?',
      okText: 'Đồng ý',
      cancelText: 'Hủy bỏ',
      onOk: async () => {
        try {
          await removeProduct(selectedRowsKeys);
          setSelectedRowsKeys({ ...selectedRowsKeys, idList: [] });
          handleSearch();
          message.success("Cập nhật thành công");
        } catch (error) {
          message.error(
            error?.response?.data?.error?.message ??
            "Lỗi hệ thống"
          );
        }
      }
    });
  };
  const handleGetListProduct = async () =>{
    setIsLoadingList(true);
    const results  = await searchListProductApi({
      ...filterModel,
      page: 1,
      pageSize:10,
    });
    setProducts(results.data);
    setIsUpdateSort(true);
    setIsLoadingList(false);
  }
  const gotoPage = async (page: number, pageSize:number) => {
    setIsLoadingList(true);
    const result = await searchProductApi({
      ...filterModel,
      page,
      pageSize,
    });
    result.data.results.forEach((m) => (m.key = m.id.toString()));
    setPagedResults(result.data);
    setIsLoadingList(false);
  };
  const handleSearch = useDebouncedCallback(() => {
    gotoPage(1,20);
  }, 500);

  const _handleClickVariationModal = async (product: ProductModel) => {
    const res = await getAllVariationsApi(product.id);
    setVariationModal({ ...variationModal, model: res.data, visible: true });
  };

  const columns: ColumnsType<ProductModel> = [
    {
      title: "Id",
      dataIndex: "id",
      key:"id",
      width:"40px",
      render: (id: string, item) => (
        <React.Fragment>
         {id}
        </React.Fragment>
      ),
    },
    {
      title: "Mã hàng",
      dataIndex: "skuCode",
      key: "skuCode",
      width: "150px",
      render: (skuCode: string, item) => skuCode,
    },
    {
      title: "Tên sản phẩm",
      dataIndex: "websiteName",
      key: "websiteName",
      render: (websiteName: string, item) => (
        <React.Fragment>
          {item.isVariation ? (
            <Link
              onClick={(e) => {
                _handleClickVariationModal(item);
              }}
              to="#"
            >
              <Avatar
                src={redirectFileLocalUrl(
                  item.images.length > 0 ? item.images[0] : ""
                )}
                shape="square"
              />{" "}
              {item.name}
            </Link>
          ) : (
            <Link to={`${UPDATE_PRODUCT_ROUTE}/${item.id}`}>
              <Avatar
                src={redirectFileLocalUrl(
                  item.images.length > 0 ? item.images[0] : ""
                )}
                shape="square"
              />{" "}
              {item.name}
            </Link>
          )}
        </React.Fragment>
      ),
    },
    {
      title: "Giá bán",
      dataIndex: "websiteName",
      key: "websiteName",
      render: (websiteName: string, item) => formatMoney(item.price),
    },
  ];

  useEffect(() => {
    handleSearch();
  }, [filterModel]);

  useEffect(() => {
    getProductCategories();
    document.title = "Danh sách sản phẩm";
  }, []);

  return (
    <div className="product">
      <Row gutter={[16, 16]} className="mb-4">
        <Col span={6}>
          <Input.Search
            value={filterModel.keyword}
            placeholder={"Nhập từ khóa..."}
            onChange={(e) => {
              setFilterModel({ ...filterModel, keyword: e.target.value });
            }}
          ></Input.Search>
        </Col>
        <Col span={6}>
          <Select
            className="w-100"
            placeholder="Chọn danh mục"
            onChange={(value: number) => {
              setFilterModel({
                ...filterModel,
                categoryIds: value ? [value] : [],
              });
            }}
            allowClear
            value={
              filterModel?.categoryIds && filterModel?.categoryIds.length > 0
                ? filterModel?.categoryIds[0]
                : undefined
            }
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {productCategories &&
              productCategories.map((productCate) => (
                <Option key={productCate.id} value={productCate.id}>
                  {productCate.name}
                </Option>
              ))}
          </Select>
        </Col>
        <Col span={6} className="d-flex align-items-center"></Col>
        <Col span={6} className="d-flex justify-content-end">
          <Space>
            <Link to={ADD_PRODUCT_ROUTE}>
              <Button type="primary" icon={<PlusCircleOutlined />}>
                Thêm sản phẩm
              </Button>
            </Link>
            <Button
              onClick={() => {
                handleGetListProduct();
                
              }}
              type="default"
              icon={<OrderedListOutlined />}
            >
              {"Sắp xếp"}
            </Button>
          </Space>
        </Col>
      </Row>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={pagedResults.results}
        size="small"
        pagination={{
          total: pagedResults.totalItemCount,
          pageSize: pagedResults.pageSize,
          current: pagedResults.currentPage,
          onChange: gotoPage,
        }}
        loading={isLoadingList}
      ></Table>
      <VariationModal
        visible={variationModal.visible}
        onClose={variationModal.onClose}
        model={variationModal.model}
        onReloadModel={_handleClickVariationModal}
      />
      <Sortable
        model={
          products.length > 0
            ? products.map((item) => ({
                key: item.id.toString(),
                title: item.name,
                sort: item.sort ?? 0,
              }))
            : []
        }
        onCancel={async () => {
          setIsUpdateSort(false);
        }}
        onOk={handleSortProduct}
        title={`Sắp xếp thứ tự sản phẩm`}
        visible={isUpdateSort}
      ></Sortable>
        <Drawer
        height="70px"
        mask={false}
        placement="bottom"
        closable={true}
        title="Bạn có muốn xóa không ?"
        onClose={onEmptySelection}
        visible={
          selectedRowsKeys !== undefined &&
            selectedRowsKeys.idList !== undefined &&
            selectedRowsKeys.idList.length > 0
            ? true
            : false
        }
        extra={<Button
          type="primary"
          danger={true}
          onClick={onRemoveBtn}
          icon={<DeleteOutlined />}
        >
          Xóa {selectedRowsKeys.idList.length.toString()} dòng
        </Button>}
      >

      </Drawer>
    </div>
  );
};

export default Product;
